// Modal for creating a new meet room. Extends shared-gov's Modal shell.
// Submit calls createRoom which inserts the row, optimistically merges
// into root.rooms, and routes to /meet/:id.
//
// IMPORTANT: reactive getters below route through `_draft(s)` so they
// never throw on the first-pass signal evaluation (when root state may
// not have `newRoomDraft` yet). Optional chaining on `s.root?.X` has
// still been observed to throw `Cannot read properties of undefined
// (reading 'newRoomDraft')` inside smbls v3.14 signal effects, which is
// why we explicitly guard with a try-block helper instead of relying on
// `?.` alone.

const _draft = (s) => {
  try {
    if (!s) return {}
    const root = s.root
    if (!root) return {}
    return root.newRoomDraft || {}
  } catch {
    return {}
  }
}

const _patch = (s, patch) => {
  try {
    if (!s || !s.root) return
    const current = s.root.newRoomDraft || {}
    s.root.update({ newRoomDraft: { ...current, ...patch } })
  } catch (err) {
    console.warn('[MeetNewRoomModal] patch failed', err)
  }
}

export const MeetNewRoomModal = {
  extends: 'Modal',
  stateKey: 'showNewRoom',
  extraClose: { newRoomDraft: null },

  Window: {
    width: 'I',
    maxWidth: '90vw',
    gap: 'A',
    ModalHeader: {
      Hgroup: {
        H: { text: 'New call' },
        P: { text: 'Start a video call. Optionally enable AI transcription — the call ends with a transcript page that proposes calendar events, tickets, notes, and DMs to follow up on.' }
      }
    },

    NameField: {
      flow: 'y',
      gap: 'V',

      Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'NAME' },
      Input: {
        tag: 'input',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'document',
        color: 'title',
        fontSize: 'Z2',
        placeholder: 'e.g. Weekly product sync',
        value: (el, s) => _draft(s).name || '',
        onInput: (e, el, s) => _patch(s, { name: e.target.value })
      }
    },

    TranscribeField: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',
      padding: 'Z 0',

      Label: {
        flow: 'y',
        gap: 'V',
        flex: '1',

        Title: { tag: 'span', fontSize: 'Z2', color: 'title', fontWeight: '500', text: 'AI transcription' },
        Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'Records text only — never raw audio. Decided at creation, not toggleable mid-call.' }
      },

      Toggle: {
        tag: 'button',
        padding: 'V Z',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        theme: (el, s) => _draft(s).transcribeEnabled ? 'primary' : 'secondary',
        text: (el, s) => _draft(s).transcribeEnabled ? 'On' : 'Off',
        onClick: (e, el, s) => _patch(s, { transcribeEnabled: !_draft(s).transcribeEnabled })
      }
    },

    PrivateField: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',
      padding: 'Z 0',

      Label: {
        flow: 'y',
        gap: 'V',
        flex: '1',

        Title: { tag: 'span', fontSize: 'Z2', color: 'title', fontWeight: '500', text: 'Private' },
        Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'Only you and explicitly added members can join or read the transcript.' }
      },

      Toggle: {
        tag: 'button',
        padding: 'V Z',
        round: 'Z',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        theme: (el, s) => _draft(s).isPrivate ? 'primary' : 'secondary',
        text: (el, s) => _draft(s).isPrivate ? 'On' : 'Off',
        onClick: (e, el, s) => _patch(s, { isPrivate: !_draft(s).isPrivate })
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',

      StartBtn: {
        extends: 'Button',
        theme: 'primary',
        text: 'Start call',
        onClick: async (e, el, s) => {
          const d = _draft(s)
          const name = (d.name || '').trim()
          if (!name) return
          const id = await el.call('createRoom', {
            name,
            kind: 'room',
            isPrivate: !!d.isPrivate,
            transcribeEnabled: !!d.transcribeEnabled
          })
          if (!id) return
          if (s && s.root) s.root.update({ activeRoomId: id, meetView: 'call' })
          if (typeof window !== 'undefined') {
            history.replaceState(null, '', `#room=${id}&view=call`)
          }
          el.call('connectCall', id)
        }
      }
    }
  }
}
